// src/pages/Courses/CoursesPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./CoursesPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001/api/v1";

/* ════════════════════════════════════════════
   TYPE  — single source of truth for both
   Courses and Live Classes (from same collection)
════════════════════════════════════════════ */
export interface ICourse {
  _id:              string;
  slug:             string;
  course_slug:             string;
  title:            string;
  description:      string;
  category:         string;
  category_key:     string;
  instructor:       string;
  instructor_title: string;
  instructor_avatar:string;
  instructor_bio:   string;

  /* Type flags */
  is_live:          boolean;
  is_recorded:      boolean;
  is_bestseller:    boolean;

  /* Live-class specific fields (null/undefined for recorded courses) */
  scheduled_at?:        string;   // ISO – when the live class starts
  duration_minutes?:    number;   // for live classes
  status?:              "scheduled" | "live" | "ended" | "cancelled";
  max_participants?:    number;
  enrolled_count?:      number;   // denormalised count stored in DB
  zoom_join_url?:       string;
  recording_url?:       string;
  recording_available?: boolean;
  is_free?:             boolean;
  tags?:                string[];

  /* Shared */
  rating:           number;
  review_count:     number;
  students_count:   number;
  students_display: string;
  duration_display: string;
  duration_weeks:   number;
  level:            "Beginner" | "Intermediate" | "Advanced";
  price:            number;
  original_price:   number;
  image_url:        string;
  what_you_learn:   string[];
  includes:         { icon: string; text: string }[];
  modules: {
    module_id: number;
    title:     string;
    lessons:   {
      lesson_id:    string;
      title:        string;
      subtitle:     string;
      video_url:    string;
      duration_sec: number;
      is_preview:   boolean;
      order:        number;
      poster:    string;
    }[];
  }[];
}

const CATEGORIES = [
  { key: "all",         label: "All Courses",    emoji: "📚" },
  { key: "kids",        label: "Kids Coding",    emoji: "🎮" },
  { key: "engineering", label: "Engineering",    emoji: "⚙️"  },
  { key: "medical",     label: "Medical",        emoji: "🩺" },
  { key: "sscbanking",  label: "SSC/Banking",    emoji: "🏛️"  },
  { key: "programming", label: "Programming",    emoji: "💻" },
  { key: "languages",   label: "Languages",      emoji: "🗣️"  },
  { key: "school",      label: "School",         emoji: "🎓" },
];

/* ── Icons ── */
const IcoStar    = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="#fbbf24" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>);
const IcoUsers   = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>);
const IcoClock   = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const IcoSearch  = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const IcoLive    = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="2" y="7" width="20" height="15" rx="2"/><polyline points="17 2 12 7 7 2" stroke="currentColor" fill="none" strokeWidth="2"/></svg>);
const IcoRecord  = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4" fill="white"/></svg>);
const IcoArrow   = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>);

const SkeletonCard = () => (
  <div className="cp-skeleton-card">
    <div className="cp-skel-img" />
    <div className="cp-skel-body">
      <div className="cp-skel-line short" />
      <div className="cp-skel-line" />
      <div className="cp-skel-line medium" />
    </div>
  </div>
);

/* Live class mini card for homepage section */
const LiveMiniCard = ({ lc }: { lc: ICourse }) => {
  const isLive = lc.status === "live";
  const diff   = lc.scheduled_at ? new Date(lc.scheduled_at).getTime() - Date.now() : 0;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const timeLabel = isLive ? "LIVE NOW" : diff <= 0 ? "Starting soon" : h > 0 ? `in ${h}h ${m}m` : `in ${m} min`;

  return (
    <Link to={`/live-classes/${lc.slug}`} className="cp-live-card-link">
      <div className={`cp-live-card${isLive ? " cp-live-card-active" : ""}`}>
        <div className="cp-live-card-img-wrap">
          <img src={lc.image_url} alt={lc.title} className="cp-live-card-img" loading="lazy" />
          {isLive && (
            <span className="cp-live-badge">
              <span className="cp-live-dot" />LIVE
            </span>
          )}
          {!isLive && (
            <span className="cp-upcoming-badge">📅 Upcoming</span>
          )}
        </div>
        <div className="cp-live-card-body">
          <div className="cp-live-card-cat">{lc.category}</div>
          <h3 className="cp-live-card-title">{lc.title}</h3>
          <div className="cp-live-card-instructor">
            <div className="cp-live-instr-avatar">{lc.instructor_avatar}</div>
            <span className="cp-live-instr-name">{lc.instructor}</span>
          </div>
          <div className={`cp-live-card-time${isLive ? " live" : ""}`}>
            {isLive ? (
              <><span className="cp-live-dot-sm" /> {timeLabel} · {lc.enrolled_count || 0} joined</>
            ) : (
              <><IcoClock /> <strong>{timeLabel}</strong> · {lc.duration_minutes || 60} min</>
            )}
          </div>
          <div className="cp-live-card-cta">
            {isLive
              ? <span className="cp-live-cta-join">🔴 Join Now</span>
              : <span className="cp-live-cta-enroll">{lc.is_free ? "Enroll Free" : "Enroll Now"}</span>
            }
          </div>
        </div>
      </div>
    </Link>
  );
};

/* ════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════ */
const CoursesPage: React.FC = () => {
  const [courses,        setCourses]        = useState<ICourse[]>([]);
  const [liveClasses,    setLiveClasses]    = useState<ICourse[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery,    setSearchQuery]    = useState("");
  const [typeFilter,     setTypeFilter]     = useState<"all"|"live"|"recorded">("all");

  /* ── Fetch recorded courses ── */
  useEffect(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (activeCategory !== "all") params.set("category_key", activeCategory);
    if (typeFilter === "live")     params.set("is_live",     "true");
    if (typeFilter === "recorded") params.set("is_recorded", "true");
    if (searchQuery.trim())        params.set("search",      searchQuery.trim());

    fetch(`${API_BASE}/courses?${params.toString()}`)
      .then(r => r.json())
      .then(d => { if (d.success) setCourses(d.data); else setError("Failed to load courses"); })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setLoading(false));
  }, [activeCategory, typeFilter, searchQuery]);

  /* ── Fetch live classes for homepage section ── */
  useEffect(() => {
    fetch(`${API_BASE}/courses?is_live=true`)
      .then(r => r.json())
      .then(d => { if (d.success) setLiveClasses(d.data.slice(0, 4)); })
      .catch(() => {});
  }, []);

  const liveCount = liveClasses.filter(c => c.status === "live").length;

  const stats = useMemo(() => [
    { val: `${courses.filter(c => !c.is_live).length}+`, label: "Courses" },
    { val: `${liveClasses.length}`,                       label: "Live Classes" },
    { val: `${Math.round(courses.reduce((s,c) => s + c.students_count, 0)/1000)}K+`, label: "Students" },
    { val: courses.length > 0 ? (courses.reduce((s,c) => s + c.rating, 0) / courses.length).toFixed(1) : "4.8", label: "Avg Rating" },
  ], [courses, liveClasses]);

  return (
    <>
      <Helmet>
        <title>Online Courses – Acadebility</title>
        <meta name="description" content="Live and recorded courses for JEE, NEET, SSC, Banking, Kids Coding and more." />
      </Helmet>

      <div className="cp-page">

        {/* ════════ HERO ════════ */}
        <div className="cp-hero">
          <div className="cp-hero-inner">
            <h1 className="cp-hero-title">Learn from the Best Online Classes</h1>
            <p className="cp-hero-sub">
              Live interactive classes, expert instructors, and lifetime access to recorded sessions.<br />
              Start your learning journey today!
            </p>
            <div className="cp-stats-row">
              {stats.map(s => (
                <div className="cp-stat-card" key={s.label}>
                  <div className="cp-stat-val">{s.val}</div>
                  <div className="cp-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="cp-search-wrap">
              <IcoSearch />
              <input className="cp-search-input" placeholder="Search courses, instructors, or topics..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              {searchQuery && <button className="cp-search-clear" onClick={() => setSearchQuery("")}>✕</button>}
            </div>
          </div>
        </div>

        {/* ════════ LIVE CLASSES SECTION ════════ */}
        {liveClasses.length > 0 && (
          <div className="cp-live-section">
            <div className="cp-live-section-inner">
              <div className="cp-live-section-header">
                <div className="cp-live-section-title-wrap">
                  {liveCount > 0 && (
                    <span className="cp-live-now-pill">
                      <span className="cp-live-dot" />{liveCount} Live Now
                    </span>
                  )}
                  <h2 className="cp-live-section-title">🔴 Live Interactive Classes</h2>
                  <p className="cp-live-section-sub">Join in real-time, ask questions, get instant answers from India's best educators</p>
                </div>
                <Link to="/live-classes" className="cp-view-all-btn">
                  View All <IcoArrow />
                </Link>
              </div>

              <div className="cp-live-grid">
                {liveClasses.map(lc => <LiveMiniCard key={lc._id} lc={lc} />)}
              </div>

              <div className="cp-live-footer">
                <Link to="/live-classes" className="cp-live-footer-link">
                  See all live classes &amp; upcoming sessions →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* ════════ BODY ════════ */}
        <div className="cp-body">
          <div className="cp-section-title">Browse Recorded Courses</div>
          <div className="cp-cats-scroll">
            {CATEGORIES.map(cat => (
              <button key={cat.key} className={`cp-cat-btn${activeCategory === cat.key ? " active" : ""}`} onClick={() => setActiveCategory(cat.key)}>
                <span className="cp-cat-emoji">{cat.emoji}</span>{cat.label}
              </button>
            ))}
          </div>

          <div className="cp-results-bar">
            <span className="cp-results-count">
              {loading ? "Loading…" : <><strong>{courses.length}</strong> course{courses.length !== 1 ? "s" : ""}</>}
            </span>
            <div className="cp-type-filters">
              <button className={`cp-type-btn${typeFilter==="all"      ? " active":""}`} onClick={() => setTypeFilter("all")}>All</button>
              <button className={`cp-type-btn cp-type-live${typeFilter==="live"     ? " active":""}`} onClick={() => setTypeFilter("live")}><IcoLive /> Live Classes</button>
              <button className={`cp-type-btn cp-type-rec${typeFilter==="recorded" ? " active":""}`} onClick={() => setTypeFilter("recorded")}><IcoRecord /> Recorded</button>
            </div>
          </div>

          {error && (
            <div className="cp-error"><span>⚠️ {error}</span><button onClick={() => setSearchQuery(p => p)}>Retry</button></div>
          )}

          {loading ? (
            <div className="cp-grid">{Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}</div>
          ) : courses.length === 0 && !error ? (
            <div className="cp-empty">
              <span style={{ fontSize:"3rem" }}>🔍</span>
              <h3>No courses found</h3>
              <p>Try adjusting your search or category filter</p>
            </div>
          ) : (
            <div className="cp-grid">
              {courses.map(course => (
                <Link to={`/courses/${course.slug}`} key={course._id} className="cp-card-link">
                  <div className="cp-card">
                    <div className="cp-card-img-wrap">
                      <img src={course.image_url} alt={course.title} className="cp-card-img" loading="lazy" />
                      <div className="cp-card-badges">
                        {course.is_bestseller && <span className="cp-badge cp-badge-bestseller">🏆 Bestseller</span>}
                        {course.is_live       && <span className="cp-badge cp-badge-live"><IcoLive /> Live</span>}
                        {course.is_recorded   && <span className="cp-badge cp-badge-recorded"><IcoRecord /> Recorded</span>}
                      </div>
                      <div className="cp-card-price-overlay">
                        <div className="cp-card-price-disc">₹{course.price.toLocaleString("en-IN")}</div>
                        <div className="cp-card-price-orig">₹{course.original_price.toLocaleString("en-IN")}</div>
                      </div>
                    </div>
                    <div className="cp-card-body">
                      <div className="cp-card-cat">{course.category}</div>
                      <h3 className="cp-card-title">{course.title}</h3>
                      <p className="cp-card-instructor">{course.instructor}</p>
                      <div className="cp-card-meta">
                        <span className="cp-card-rating"><IcoStar /> <strong>{course.rating}</strong><span className="cp-card-rating-count">({course.review_count.toLocaleString("en-IN")})</span></span>
                        <span className="cp-card-students"><IcoUsers /> {course.students_display}</span>
                      </div>
                      <div className="cp-card-footer">
                        <span className="cp-card-duration"><IcoClock /> {course.duration_display}</span>
                        <span className={`cp-card-level cp-level-${course.level.toLowerCase()}`}>{course.level}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CoursesPage;
